import { useCallback, useEffect, useRef, useState } from 'react';

const useBrowserHistoryState = <State = any>() => {
    const [state, setState] = useState<State>();

    useEffect(() => {
        setState(window.history.state);
        const updateStateFromCustomEvent = (event: CustomEvent<State> | PopStateEvent) => {
            const nativeEventState = (event as PopStateEvent).state;
            const customEventState = (event as CustomEvent<State>).detail;
            setState(customEventState || nativeEventState);
        };

        window.addEventListener('popstate', updateStateFromCustomEvent);

        return () => window.removeEventListener('popstate', updateStateFromCustomEvent);
    }, []);

    const push = useCallback((data: State) => {
        const newState = { ...window.history.state, ...data };
        window.history.pushState(newState, '');
        window.dispatchEvent(new CustomEvent<State>('popstate', { detail: newState }));
    }, []);

    const replace = useCallback((data: State) => {
        const newState = { ...window.history.state, ...data };
        window.history.replaceState(newState, '');
        window.dispatchEvent(new CustomEvent<State>('popstate', { detail: newState }));
    }, []);

    return { state, push, replace };
};

type StateShape<D = undefined> = {
    [modalName: string]: { isOpen: boolean; data?: D };
};

export const useBrowserModalControls = <D>(modalName: string) => {
    const { state, push, replace } = useBrowserHistoryState<StateShape<D>>();
    const modalState = state?.[modalName];
    const returnFocusRef = useRef<HTMLElement | null>(null);
    const lastInteractionWasKeyboardRef = useRef<boolean>(false);
    const previousIsOpenRef = useRef<boolean>(false);
    const isModalOpen = Boolean(modalState?.isOpen);

    useEffect(() => {
        const handleKeyDown = () => {
            lastInteractionWasKeyboardRef.current = true;
        };

        const handleMouseDown = () => {
            lastInteractionWasKeyboardRef.current = false;
        };

        if (isModalOpen) {
            previousIsOpenRef.current = true;
            window.addEventListener('keydown', handleKeyDown, true);
            window.addEventListener('mousedown', handleMouseDown, true);
        } else if (previousIsOpenRef.current) {
            if (lastInteractionWasKeyboardRef.current && returnFocusRef.current) {
                const elementToFocus = returnFocusRef.current;
                requestAnimationFrame(() => {
                    if (document.contains(elementToFocus)) {
                        elementToFocus.focus();
                    }
                });
            }

            returnFocusRef.current = null;
            lastInteractionWasKeyboardRef.current = false;
            previousIsOpenRef.current = false;
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
            window.removeEventListener('mousedown', handleMouseDown, true);
        };
    }, [isModalOpen]);

    const closeModal = useCallback(() => {
        window.history.back();
        // Manually updating state because `history.back()` does not synchronize with `history.state`
        // So there might be race condition issues if `closeModal` and `openModal` are called in quick succession
        const updatedState = { ...window.history.state };
        updatedState[modalName] = { isOpen: false };
        window.dispatchEvent(new CustomEvent<D>('popstate', { detail: updatedState }));
    }, [modalName]);

    const openModal = useCallback(
        <F extends HTMLElement = HTMLElement>(data?: D, returnFocusNode?: F) => {
            returnFocusRef.current = returnFocusNode ?? null;
            lastInteractionWasKeyboardRef.current = false;
            push({ [modalName]: { isOpen: true, data } });
        },
        [modalName, push],
    );

    const editModal = useCallback(
        (isOpen: boolean, data: D | undefined = modalState?.data) => replace({ [modalName]: { isOpen, data } }),
        [modalName, modalState?.data, replace],
    );
    const closeModalWithNewEntry = useCallback(() => push({ [modalName]: { isOpen: false } }), [modalName, push]);

    return {
        isModalOpen,
        modalData: modalState?.data,
        closeModal,
        openModal,
        editModal,
        closeModalWithNewEntry,
    };
};
